import { Suspense, lazy } from 'react'
import { Box, Group, Skeleton, Stack } from '@mantine/core'
import type { MentionablePerson } from './RichTextEditor'

/**
 * The editor, loaded only when one is actually shown.
 *
 * TipTap and ProseMirror are the heaviest dependency in the client by some
 * margin, and nothing outside a task dialog or a comment box needs them.
 * Importing through here keeps them out of every other page's download rather
 * than taxing a dashboard visit for an editor nobody opened.
 */
const load = () => import('./RichTextEditor')

const Editor = lazy(async () => ({ default: (await load()).RichTextEditor }))

/**
 * Fetches the editor chunk before anybody asks for it.
 *
 * Called once the first screen is idle. The chunk is ~114KB gzipped, and
 * waiting for it is the difference between a task dialog opening instantly
 * and opening with a placeholder where the description should be. Fetching it
 * while nothing else is happening costs the user nothing and removes the wait
 * entirely.
 */
export function preloadEditor(): void {
  const start = () => void load().catch(() => undefined)

  const idle = (window as Window & {
    requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number
  }).requestIdleCallback

  if (idle) {
    idle(start, { timeout: 3_000 })
  } else {
    // Safari has no requestIdleCallback; a short delay is close enough, and
    // the point is only to stay out of the first screen's way.
    window.setTimeout(start, 1_500)
  }
}

export type { MentionablePerson }

export function RichTextField(props: {
  value: string
  onChange: (html: string) => void
  people?: MentionablePerson[]
  onSearchPeople?: (search: string) => Promise<MentionablePerson[]>
  placeholder?: string
  minHeight?: number
  autoFocus?: boolean
  onSubmit?: () => void
  readOnly?: boolean
  onAttach?: (files: FileList) => void
  attaching?: boolean
}) {
  const minHeight = props.minHeight ?? 120

  return (
    <Suspense
      fallback={
        // Reserves the editor's height, so the surrounding dialog does not
        // jump once the chunk arrives.
        <Box
          style={{
            minHeight: minHeight + 42,
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 'var(--mantine-radius-md)',
            overflow: 'hidden',
          }}
        >
          {/* The editor's own shape - a toolbar and a few lines - rather than
              a spinner saying "loading editor", which is a sentence nobody
              needs to read. Usually never seen: the chunk is preloaded once
              the first screen goes idle. */}
          <Group gap={6} p={6} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            {[24, 24, 24, 34, 24, 28].map((width, index) => (
              <Skeleton key={index} height={22} width={width} radius="sm" />
            ))}
          </Group>
          <Stack gap={8} p="sm">
            <Skeleton height={9} radius="sm" />
            <Skeleton height={9} width="75%" radius="sm" />
          </Stack>
        </Box>
      }
    >
      <Editor {...props} />
    </Suspense>
  )
}
